import { Component } from '@theme/component';

/**
 * Parses a comma-separated string into an array of trimmed, non-empty words.
 *
 * @param {string} str - Comma-separated words
 * @returns {string[]}
 */
function parseWords(str) {
  if (!str) return [];
  return str
    .split(',')
    .map((w) => w.trim())
    .filter((w) => w.length > 0);
}

const STATE_TYPING = 'typing';
const STATE_PAUSED = 'paused';
const STATE_DELETING = 'deleting';
const STATE_DONE = 'done';

/**
 * Typed logo component that creates a typewriter animation effect.
 *
 * @extends {Component}
 */
export class TypedLogoComponent extends Component {
  requiredRefs = ['typed', 'cursor'];

  connectedCallback() {
    super.connectedCallback();

    this.words = parseWords(this.dataset.words || '');
    this.typingSpeed = parseInt(this.dataset.typingSpeed, 10) || 100;
    this.deleteSpeed = parseInt(this.dataset.deleteSpeed, 10) || 50;
    this.pauseDuration = parseInt(this.dataset.pause, 10) || 1500;
    this.loop = this.dataset.loop === 'true';

    this.wordIndex = 0;
    this.charIndex = 0;
    this.state = STATE_TYPING;
    this.timerId = null;

    // Ensure typed area is empty before animation starts
    const { typed } = this.refs;
    if (typed) typed.textContent = '';

    const startDelay = 800;

    if (this.words.length > 0) {
      const startTyping = () => {
        this.timerId = setTimeout(() => this.tick(), startDelay);
      };

      if (document.readyState === 'complete') {
        startTyping();
      } else {
        window.addEventListener('load', () => {
          startTyping();
        }, { once: true });
      }
    }
  }

  disconnectedCallback() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  tick() {
    const word = this.words[this.wordIndex];
    if (!word) return;

    const { typed } = this.refs;

    switch (this.state) {
      case STATE_TYPING: {
        this.charIndex++;
        typed.textContent = word.slice(0, this.charIndex);

        if (this.charIndex >= word.length) {
          const isLastWord = this.wordIndex >= this.words.length - 1;

          if (isLastWord && !this.loop) {
            this.state = STATE_DONE;
            const { cursor } = this.refs;
            if (cursor) cursor.style.display = 'none';
            return;
          }

          this.state = STATE_PAUSED;
          this.timerId = setTimeout(() => this.tick(), this.pauseDuration);
          return;
        }

        this.timerId = setTimeout(() => this.tick(), this.typingSpeed);
        break;
      }

      case STATE_PAUSED: {
        this.state = STATE_DELETING;
        this.tick();
        break;
      }

      case STATE_DELETING: {
        this.charIndex--;
        typed.textContent = word.slice(0, this.charIndex);

        if (this.charIndex <= 0) {
          this.wordIndex = (this.wordIndex + 1) % this.words.length;
          this.charIndex = 0;
          this.state = STATE_TYPING;
          this.timerId = setTimeout(() => this.tick(), this.typingSpeed);
          return;
        }

        this.timerId = setTimeout(() => this.tick(), this.deleteSpeed);
        break;
      }

      case STATE_DONE:
        break;
    }
  }
}

if (!customElements.get('typed-logo-component')) {
  customElements.define('typed-logo-component', TypedLogoComponent);
}
